import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Send, Mail, CheckCircle, XCircle, Clock, Loader2, Zap, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Dashboard = () => {
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("Message from Delta AI Academy");
  const [templateId, setTemplateId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total_sent: 0,
    total_failed: 0,
    total_pending: 0,
    total_delivered: 0,
    total_bounced: 0,
    recent_emails: []
  });

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/email/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchTemplates();
  }, []);

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter a recipient email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/email/send`, {
        recipient_email: email,
        subject: subject,
        template_id: templateId === "default" ? null : templateId || null
      });
      toast.success(`Email sent to ${email}`);
      setEmail("");
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: "Sent", value: stats.total_sent, icon: Send, color: "text-cyan-400", bg: "bg-cyan-500/10" },
    { label: "Delivered", value: stats.total_delivered, icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/10" },
    { label: "Failed", value: stats.total_failed, icon: XCircle, color: "text-red-400", bg: "bg-red-500/10" },
    { label: "Bounced", value: stats.total_bounced, icon: AlertTriangle, color: "text-orange-400", bg: "bg-orange-500/10" },
    { label: "Pending", value: stats.total_pending, icon: Clock, color: "text-amber-400", bg: "bg-amber-500/10" },
  ];

  const getStatusBadge = (status) => {
    const badges = {
      sent: 'badge-sent',
      delivered: 'badge-delivered',
      pending: 'badge-pending',
      failed: 'badge-failed',
      bounced: 'badge-bounced'
    };
    return badges[status] || 'badge-pending';
  };

  return (
    <div className="p-6 md:p-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">
          Dashboard
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Send emails to your audience
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat) => (
          <Card key={stat.label} className="bg-[#111] border-[#1a1a1a] rounded-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono text-slate-500 uppercase tracking-wider">
                    {stat.label}
                  </p>
                  <p className="text-2xl font-heading font-bold text-white mt-1">
                    {stat.value}
                  </p>
                </div>
                <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Send Email Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-[#111] border-[#1a1a1a] rounded-lg glow-cyan">
          <CardHeader className="border-b border-[#1a1a1a] pb-4">
            <CardTitle className="font-heading text-xl font-semibold flex items-center gap-2 text-white">
              <Zap className="w-5 h-5 text-cyan-400" />
              Quick Send
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSendEmail} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-400">
                  Recipient Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-10 rounded-lg border-[#222] bg-[#0d0d0d] text-white focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 font-mono text-sm"
                  data-testid="input-recipient-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject" className="text-sm font-medium text-slate-400">
                  Subject
                </Label>
                <Input
                  id="subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="h-10 rounded-lg border-[#222] bg-[#0d0d0d] text-white focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 text-sm"
                  data-testid="input-subject"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-400">
                  Template (Optional)
                </Label>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger className="h-10 rounded-lg border-[#222] bg-[#0d0d0d] text-white" data-testid="select-template">
                    <SelectValue placeholder="Use default template" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-[#222]">
                    <SelectItem value="default" className="text-slate-300">Default Template</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id} className="text-slate-300">{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-all active:scale-95 h-10"
                data-testid="btn-send-email"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-[#111] border-[#1a1a1a] rounded-lg">
          <CardHeader className="border-b border-[#1a1a1a] pb-4">
            <CardTitle className="font-heading text-xl font-semibold text-white">
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {stats.recent_emails.length > 0 ? (
              <div className="divide-y divide-[#1a1a1a]">
                {stats.recent_emails.map((log, index) => (
                  <div 
                    key={index} 
                    className="px-6 py-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-mono text-white truncate">
                        {log.recipient_email}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {new Date(log.sent_at).toLocaleString()}
                      </p>
                    </div>
                    <span className={`ml-4 ${getStatusBadge(log.status)}`}>
                      {log.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <Mail className="w-12 h-12 mx-auto text-slate-700 mb-3" />
                <p className="text-sm">No emails sent yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
